import { HTTPError } from 'ky'
import type {
  AddOrganizationMemberRequest,
  AgentTemplateResponse,
  AuthResponse,
  BillingOverviewResponse,
  BootstrapOrganizationResponse,
  BootstrapResponse,
  ChangePasswordRequest,
  ChangePasswordResponse,
  ChatMessageResponse,
  ChatSummaryResponse,
  ClaimGtmCheckoutRequest,
  ConfirmEmailChangeResponse,
  ConfluencePluginConfig,
  ContextAuditClearHistoryResponse,
  ContextAuditDraftListResponse,
  ContextAuditDrilldownResponse,
  ContextAuditPreviewResponse,
  ContextAuditReportDetailResponse,
  ContextAuditReportListResponse,
  ContextAuditScanResponse,
  ContextAuditTrendResponse,
  CreateChatRequest,
  CreateChatResponse,
  CreateGtmCheckoutRequest,
  CreateGtmCheckoutResponse,
  CreateGtmDemoRunRequest,
  CreateGtmDraftRequest,
  CreateOrganizationAgentRequest,
  CreateOrganizationAgentTriggerRequest,
  CreateOrganizationRequest,
  CreateWorkspaceRuleRequest,
  CreateWorkspaceRequest,
  CreditTopUpItem,
  DecideChatActionApprovalRequest,
  DecideChatActionApprovalResponse,
  DeleteAccountRequest,
  DemoRequestRequest,
  DemoRequestResponse,
  EmailChangeStatusResponse,
  GenerateOrganizationAgentMetadataRequest,
  GenerateOrganizationAgentMetadataResponse,
  GitHubPluginConfig,
  GtmCheckoutResponse,
  GtmConversationSummary,
  GtmDemoRunResponse,
  GtmEmailPreview,
  GtmEmailPreviewRequest,
  GtmInboxRefreshResponse,
  GtmLeadStage,
  GtmOutreachPlan,
  GtmProject,
  GtmProjectCreation,
  GtmReplyResult,
  GtmSenderStatus,
  GtmSendingSettings,
  GtmUsageOverview,
  GtmUsageSummary,
  GtmWorkspaceAnalysis,
  GtmWorkspaceCampaign,
  GtmWorkspaceEmailSettings,
  GtmWorkspaceLead,
  GtmWorkspaceLeadDetail,
  GtmWorkspaceOverview,
  GtmWorkspaceSearch,
  InviteResponse,
  JoinRequestActionResponse,
  JoinRequestResponse,
  LeaveOrganizationResponse,
  LoginRequest,
  LoginResponse,
  LogoutResponse,
  McpAuthorizeRequest,
  McpAuthorizeResponse,
  McpConnectionResponse,
  McpSetupResponse,
  OrganizationAgentResponse,
  OrganizationAgentSchedulePreviewRequest,
  OrganizationAgentSchedulePreviewResponse,
  OrganizationAgentToolResponse,
  OrganizationAgentTriggerResponse,
  OrganizationAgentTriggerRunResponse,
  OrganizationDomainVerificationResponse,
  OrganizationInviteAcceptResponse,
  OrganizationInviteCreateResponse,
  OrganizationInviteStatusResponse,
  OrganizationResponse,
  PaymentCheckoutSession,
  PaymentStatusResponse,
  PaymentsConfigResponse,
  PilotApplicationRequest,
  PilotApplicationResponse,
  RequestEmailChangeRequest,
  RequestRefundRequest,
  ResendVerificationRequest,
  ResendVerificationResponse,
  SendMessageRequest,
  SendMessageResponse,
  SignupRequest,
  SignupResponse,
  StartTopUpRequest,
  TelegramLinkCodeResponse,
  TelegramLinkedChatsResponse,
  UpdateChatApprovalModeRequest,
  UpdateChatApprovalModeResponse,
  UpdateChatRequest,
  UpdateChatResponse,
  UpdateGtmCampaignBuyerRequest,
  UpdateGtmCheckoutRequest,
  UpdateGtmEmailSettingsRequest,
  UpdateGtmLeadRequest,
  UpdateGtmOutreachRequest,
  UpdateGtmSendingSettingsRequest,
  UpdateOrganizationAgentRequest,
  UpdateOrganizationAgentTriggerRequest,
  UpdateOrganizationMemberRoleRequest,
  UpdateOrganizationRequest,
  UpdateTelegramChatRequest,
  UpdateUserPreferencesRequest,
  UpdateUserProfileRequest,
  UpdateWorkspaceRequest,
  UpdateWorkspaceRuleRequest,
  VerifyEmailResponse,
  WorkspaceAdminSuccessionPreviewResponse,
  WorkspaceAdminSuccessionRequest,
  WorkspaceCalendarConnectionStatusResponse,
  WorkspaceCalendarOAuthStartRequest,
  WorkspaceCalendarOAuthStartResponse,
  WorkspaceConfluenceConnectionStatusResponse,
  WorkspaceConfluenceOAuthStartResponse,
  WorkspaceDocumentResponse,
  WorkspaceGitHubConnectionStatusResponse,
  WorkspaceGitHubFileContentResponse,
  WorkspaceGitHubOAuthStartResponse,
  WorkspaceGitHubRepositoriesResponse,
  WorkspaceGmailConnectionStatusResponse,
  WorkspaceGmailOAuthStartResponse,
  WorkspaceJiraConnectionStatusResponse,
  WorkspaceJiraOAuthStartResponse,
  WorkspaceJiraProjectsResponse,
  WorkspaceResponse,
  WorkspaceRuleListResponse,
  WorkspaceRuleResponse,
  WorkspaceUserResponse,
  WorkspaceYandexMailConnectionStatusResponse,
  WorkspaceYandexMailOAuthStartResponse,
} from '@laplace/contracts'
import { ky } from '@/shared/ky/ky-with-auth'

function normalizeJiraConfig(
  config: Partial<WorkspaceResponse['plugins']['jira']['config']> | undefined,
): WorkspaceResponse['plugins']['jira']['config'] {
  return {
    JIRA_BASE_URL:
      typeof config?.JIRA_BASE_URL === 'string' ? config.JIRA_BASE_URL : '',
    JIRA_PROJECT_KEY:
      typeof config?.JIRA_PROJECT_KEY === 'string'
        ? config.JIRA_PROJECT_KEY
        : '',
  }
}

function normalizeConfluenceConfig(
  config: Partial<ConfluencePluginConfig> | undefined,
): ConfluencePluginConfig {
  return {
    CONFLUENCE_BASE_URL:
      typeof config?.CONFLUENCE_BASE_URL === 'string'
        ? config.CONFLUENCE_BASE_URL
        : '',
  }
}

function normalizeGitHubConfig(
  config: Partial<GitHubPluginConfig> | undefined,
): GitHubPluginConfig {
  return {
    GITHUB_ORG: typeof config?.GITHUB_ORG === 'string' ? config.GITHUB_ORG : '',
    GITHUB_DEFAULT_REPO:
      typeof config?.GITHUB_DEFAULT_REPO === 'string'
        ? config.GITHUB_DEFAULT_REPO
        : '',
  }
}

function normalizeWorkspace(workspace: WorkspaceResponse): WorkspaceResponse {
  const jira = workspace.plugins.jira
  const isEnabled = jira.isEnabled ?? jira.enabled ?? false
  const confluence = workspace.plugins.confluence
  const isConfluenceEnabled =
    confluence?.isEnabled ?? confluence?.enabled ?? false
  const gmail = workspace.plugins.gmail
  const isGmailEnabled = gmail?.isEnabled ?? gmail?.enabled ?? false
  const yandexMail = workspace.plugins.yandexMail
  const isYandexMailEnabled =
    yandexMail?.isEnabled ?? yandexMail?.enabled ?? false
  const github = workspace.plugins.github
  const isGitHubEnabled = github?.isEnabled ?? github?.enabled ?? false
  const calendar = workspace.plugins.calendar
  const isCalendarEnabled = calendar?.isEnabled ?? calendar?.enabled ?? false

  return {
    ...workspace,
    plugins: {
      ...workspace.plugins,
      jira: {
        ...jira,
        config: normalizeJiraConfig(jira.config),
        isEnabled,
        // Transitional alias for still-mocked code paths.
        enabled: isEnabled,
      },
      confluence: {
        ...confluence,
        config: normalizeConfluenceConfig(confluence?.config),
        isEnabled: isConfluenceEnabled,
        enabled: isConfluenceEnabled,
      },
      gmail: {
        ...gmail,
        config: {},
        isEnabled: isGmailEnabled,
        enabled: isGmailEnabled,
      },
      yandexMail: {
        ...yandexMail,
        config: {},
        isEnabled: isYandexMailEnabled,
        enabled: isYandexMailEnabled,
      },
      github: {
        ...github,
        config: normalizeGitHubConfig(github?.config),
        isEnabled: isGitHubEnabled,
        enabled: isGitHubEnabled,
      },
      calendar: {
        ...calendar,
        config: {},
        isEnabled: isCalendarEnabled,
        enabled: isCalendarEnabled,
      },
    },
  }
}

function normalizeOrganizationType(rawType: unknown): 'TECHNICAL' | 'BUSINESS' {
  if (typeof rawType === 'string') {
    const normalized = rawType.trim().toUpperCase()
    if (normalized === 'BUSINESS') {
      return 'BUSINESS'
    }
  }

  return 'TECHNICAL'
}

function normalizeBootstrap(bootstrap: BootstrapResponse): BootstrapResponse {
  const normalizedOrganization = bootstrap.organization
    ? normalizeBootstrapOrganization(bootstrap.organization, bootstrap.user.id)
    : null

  return {
    ...bootstrap,
    organization: normalizedOrganization,
    workspaces: bootstrap.workspaces.map(normalizeWorkspace),
    ai: {
      chatModel:
        typeof bootstrap.ai.chatModel === 'string' &&
        bootstrap.ai.chatModel.length > 0
          ? bootstrap.ai.chatModel
          : 'GPT-4o',
    },
  }
}

function normalizeBootstrapOrganization(
  organization: NonNullable<BootstrapResponse['organization']>,
  userId: string,
): BootstrapOrganizationResponse {
  const candidate =
    organization as unknown as Partial<BootstrapOrganizationResponse> & {
      id: string
      type?: string
      isVisible?: boolean
      members?: Array<{ userId: string; role?: string }>
    }
  const membership = candidate.members?.find((m) => m.userId === userId)
  const normalizedMembershipRole = membership?.role?.toUpperCase()
  const inferredRole: BootstrapOrganizationResponse['role'] =
    normalizedMembershipRole === 'OWNER'
      ? 'OWNER'
      : normalizedMembershipRole === 'ADMIN'
        ? 'ADMIN'
        : 'MEMBER'
  const normalizedRole =
    candidate.role === 'OWNER' || candidate.role === 'ADMIN'
      ? candidate.role
      : candidate.role === 'MEMBER'
        ? 'MEMBER'
        : inferredRole

  return {
    id: candidate.id,
    name: candidate.name ?? null,
    domain: candidate.domain ?? null,
    domainVerification: candidate.domainVerification ?? null,
    type: normalizeOrganizationType(candidate.type),
    isVisible:
      typeof candidate.isVisible === 'boolean'
        ? candidate.isVisible
        : normalizeOrganizationType(candidate.type) === 'BUSINESS',
    role: normalizedRole,
    membershipStatus: candidate.membershipStatus ?? 'ACTIVE',
  }
}

export const apiClient = {
  mcp: {
    setup: async (): Promise<McpSetupResponse> =>
      ky.get('mcp-settings/setup').json<McpSetupResponse>(),
    connections: async (): Promise<Array<McpConnectionResponse>> =>
      ky.get('mcp-settings/connections').json<Array<McpConnectionResponse>>(),
    authorize: async (
      body: McpAuthorizeRequest,
    ): Promise<McpAuthorizeResponse> =>
      ky
        .post('mcp-settings/authorize', { json: body })
        .json<McpAuthorizeResponse>(),
    deny: async (body: McpAuthorizeRequest): Promise<McpAuthorizeResponse> =>
      ky.post('mcp-settings/deny', { json: body }).json<McpAuthorizeResponse>(),
    revoke: async (id: string): Promise<void> => {
      await ky.delete(`mcp-settings/connections/${id}`)
    },
  },
  user: {
    updateProfile: async (
      body: UpdateUserProfileRequest,
    ): Promise<{ success: boolean }> =>
      ky.patch('user/me/profile', { json: body }).json<{ success: boolean }>(),
    updatePreferences: async (
      body: UpdateUserPreferencesRequest,
    ): Promise<{ success: boolean }> =>
      ky.patch('user/me', { json: body }).json<{ success: boolean }>(),
    changePassword: async (
      body: ChangePasswordRequest,
    ): Promise<ChangePasswordResponse> =>
      ky
        .patch('user/me/password', { json: body })
        .json<ChangePasswordResponse>(),
    getDeletePreview:
      async (): Promise<WorkspaceAdminSuccessionPreviewResponse> =>
        ky.get('user/me/delete-preview').json(),
    deleteAccount: async (
      body?: DeleteAccountRequest,
    ): Promise<{ success: boolean }> =>
      ky.delete('user/me', { json: body ?? {} }).json<{ success: boolean }>(),
  },
  auth: {
    getAuth: async (): Promise<AuthResponse> =>
      ky.get('auth').json<AuthResponse>(),
    login: async (body: LoginRequest): Promise<LoginResponse> =>
      ky.post('auth/login', { json: body }).json<LoginResponse>(),
    signup: async (body: SignupRequest): Promise<SignupResponse> =>
      ky.post('auth/signup', { json: body }).json<SignupResponse>(),
    verifyEmail: async (token: string): Promise<VerifyEmailResponse> =>
      ky
        .post('auth/verify-email', { json: { token } })
        .json<VerifyEmailResponse>(),
    resendVerification: async (
      body: ResendVerificationRequest,
    ): Promise<ResendVerificationResponse> =>
      ky
        .post('auth/resend-verification', { json: body })
        .json<ResendVerificationResponse>(),
    logout: async (): Promise<LogoutResponse> =>
      ky.post('auth/logout').json<LogoutResponse>(),
    emailChangeStatus: async (): Promise<EmailChangeStatusResponse> =>
      ky.get('auth/email-change').json<EmailChangeStatusResponse>(),
    requestEmailChange: async (
      body: RequestEmailChangeRequest,
    ): Promise<EmailChangeStatusResponse> =>
      ky
        .post('auth/email-change', { json: body })
        .json<EmailChangeStatusResponse>(),
    cancelEmailChange: async (): Promise<EmailChangeStatusResponse> =>
      ky.delete('auth/email-change').json<EmailChangeStatusResponse>(),
    confirmEmailChange: async (
      token: string,
    ): Promise<ConfirmEmailChangeResponse> =>
      ky
        .post('auth/email-change/confirm', { json: { token } })
        .json<ConfirmEmailChangeResponse>(),
  },
  bootstrap: {
    get: async (): Promise<BootstrapResponse> =>
      normalizeBootstrap(
        await ky.get('user/me/bootstrap').json<BootstrapResponse>(),
      ),
  },
  chats: {
    decideActionApproval: async (
      workspaceId: string,
      chatId: string,
      approvalId: string,
      body: DecideChatActionApprovalRequest,
    ): Promise<DecideChatActionApprovalResponse> =>
      ky
        .post(
          `workspaces/${workspaceId}/chats/${chatId}/action-approvals/${approvalId}/decision`,
          { json: body, timeout: false, retry: 0 },
        )
        .json<DecideChatActionApprovalResponse>(),
    openWorkspaceChatEvents: async (
      workspaceId: string,
      signal: AbortSignal,
    ): Promise<Response> =>
      ky.get(`workspaces/${workspaceId}/chats/events/stream`, {
        signal,
        timeout: false,
        retry: 0,
      }),
    listWorkspaceChats: async (
      workspaceId: string,
    ): Promise<Array<ChatSummaryResponse>> => {
      try {
        return await ky
          .get(`workspaces/${workspaceId}/chats`)
          .json<Array<ChatSummaryResponse>>()
      } catch (error) {
        if (error instanceof HTTPError && error.response.status === 404) {
          return []
        }

        throw error
      }
    },
    createWorkspaceChat: async (
      workspaceId: string,
      body: CreateChatRequest,
    ): Promise<CreateChatResponse> =>
      ky
        .post(`workspaces/${workspaceId}/chats`, { json: body })
        .json<CreateChatResponse>(),
    updateWorkspaceChat: async (
      workspaceId: string,
      chatId: string,
      body: UpdateChatRequest,
    ): Promise<UpdateChatResponse> =>
      ky
        .patch(`workspaces/${workspaceId}/chats/${chatId}`, { json: body })
        .json<UpdateChatResponse>(),
    updateChatApprovalMode: async (
      workspaceId: string,
      chatId: string,
      body: UpdateChatApprovalModeRequest,
    ): Promise<UpdateChatApprovalModeResponse> =>
      ky
        .patch(`workspaces/${workspaceId}/chats/${chatId}/approval-mode`, {
          json: body,
        })
        .json<UpdateChatApprovalModeResponse>(),
    getChatMessages: async (
      workspaceId: string,
      chatId: string,
      params?: { limit?: number; before?: string },
    ): Promise<Array<ChatMessageResponse>> =>
      ky
        .get(`workspaces/${workspaceId}/chats/${chatId}/messages`, {
          searchParams: {
            ...(params?.limit ? { limit: params.limit } : {}),
            ...(params?.before ? { before: params.before } : {}),
          },
        })
        .json<Array<ChatMessageResponse>>(),
    sendChatMessage: async (
      workspaceId: string,
      chatId: string,
      body: SendMessageRequest,
    ): Promise<SendMessageResponse> =>
      ky
        .post(`workspaces/${workspaceId}/chats/${chatId}/messages`, {
          json: body,
        })
        .json<SendMessageResponse>(),
    deleteWorkspaceChat: async (
      workspaceId: string,
      chatId: string,
    ): Promise<void> => {
      await ky.delete(`workspaces/${workspaceId}/chats/${chatId}`)
    },
  },
  organizations: {
    create: async (
      body: CreateOrganizationRequest,
    ): Promise<OrganizationResponse> =>
      ky.post('organizations', { json: body }).json<OrganizationResponse>(),
    update: async (
      id: string,
      body: UpdateOrganizationRequest,
    ): Promise<OrganizationResponse> =>
      ky
        .patch(`organizations/${id}`, { json: body })
        .json<OrganizationResponse>(),
    list: async (): Promise<Array<OrganizationResponse>> =>
      ky.get('organizations').json<Array<OrganizationResponse>>(),
    getById: async (id: string): Promise<OrganizationResponse> =>
      ky.get(`organizations/${id}`).json<OrganizationResponse>(),
    addMember: async (
      id: string,
      body: AddOrganizationMemberRequest,
    ): Promise<OrganizationResponse> =>
      ky
        .post(`organizations/${id}/members`, { json: body })
        .json<OrganizationResponse>(),
    updateMemberRole: async (
      id: string,
      userId: string,
      body: UpdateOrganizationMemberRoleRequest,
    ): Promise<OrganizationResponse> =>
      ky
        .patch(`organizations/${id}/members/${userId}`, { json: body })
        .json<OrganizationResponse>(),
    removeMember: async (
      id: string,
      userId: string,
      body?: WorkspaceAdminSuccessionRequest,
    ): Promise<OrganizationResponse> =>
      ky
        .delete(`organizations/${id}/members/${userId}`, { json: body ?? {} })
        .json<OrganizationResponse>(),
    getMemberRemovalPreview: async (
      id: string,
      userId: string,
    ): Promise<WorkspaceAdminSuccessionPreviewResponse> =>
      ky.get(`organizations/${id}/members/${userId}/removal-preview`).json(),
    leave: async (
      id: string,
      body?: WorkspaceAdminSuccessionRequest,
    ): Promise<LeaveOrganizationResponse> =>
      ky
        .post(`organizations/${id}/leave`, { json: body ?? {} })
        .json<LeaveOrganizationResponse>(),
    getLeavePreview: async (
      id: string,
    ): Promise<WorkspaceAdminSuccessionPreviewResponse> =>
      ky.get(`organizations/${id}/leave-preview`).json(),
    getWorkspaces: async (id: string): Promise<Array<WorkspaceResponse>> =>
      (
        await ky
          .get(`organizations/${id}/workspaces`)
          .json<Array<WorkspaceResponse>>()
      ).map(normalizeWorkspace),
    getDomainVerification: async (
      id: string,
    ): Promise<OrganizationDomainVerificationResponse | null> =>
      ky
        .get(`organizations/${id}/domain-verification`)
        .json<OrganizationDomainVerificationResponse | null>(),
    createDomainVerification: async (
      id: string,
      domain: string,
    ): Promise<OrganizationDomainVerificationResponse> =>
      ky
        .post(`organizations/${id}/domain-verification`, {
          json: { domain },
        })
        .json<OrganizationDomainVerificationResponse>(),
    verifyDomain: async (
      id: string,
    ): Promise<OrganizationDomainVerificationResponse> =>
      ky
        .post(`organizations/${id}/domain-verification/verify`)
        .json<OrganizationDomainVerificationResponse>(),
    removeDomainVerification: async (id: string): Promise<void> => {
      await ky.delete(`organizations/${id}/domain-verification`)
    },
    agents: {
      listTemplates: async (
        organizationId: string,
        language: 'ru' | 'en',
      ): Promise<Array<AgentTemplateResponse>> =>
        ky
          .get(`organizations/${organizationId}/agent-templates`, {
            searchParams: { lang: language },
          })
          .json<Array<AgentTemplateResponse>>(),
      list: async (
        organizationId: string,
      ): Promise<Array<OrganizationAgentResponse>> =>
        ky
          .get(`organizations/${organizationId}/agents`)
          .json<Array<OrganizationAgentResponse>>(),
      get: async (
        organizationId: string,
        agentId: string,
      ): Promise<OrganizationAgentResponse> =>
        ky
          .get(`organizations/${organizationId}/agents/${agentId}`)
          .json<OrganizationAgentResponse>(),
      create: async (
        organizationId: string,
        body: CreateOrganizationAgentRequest,
      ): Promise<OrganizationAgentResponse> =>
        ky
          .post(`organizations/${organizationId}/agents`, { json: body })
          .json<OrganizationAgentResponse>(),
      update: async (
        organizationId: string,
        agentId: string,
        body: UpdateOrganizationAgentRequest,
      ): Promise<OrganizationAgentResponse> =>
        ky
          .patch(`organizations/${organizationId}/agents/${agentId}`, {
            json: body,
          })
          .json<OrganizationAgentResponse>(),
      remove: async (
        organizationId: string,
        agentId: string,
      ): Promise<void> => {
        await ky.delete(`organizations/${organizationId}/agents/${agentId}`)
      },
      tools: async (
        organizationId: string,
      ): Promise<Array<OrganizationAgentToolResponse>> =>
        ky
          .get(`organizations/${organizationId}/agent-tools`)
          .json<Array<OrganizationAgentToolResponse>>(),
      generateMetadata: async (
        organizationId: string,
        body: GenerateOrganizationAgentMetadataRequest,
      ): Promise<GenerateOrganizationAgentMetadataResponse> =>
        ky
          .post(`organizations/${organizationId}/agents/generate-metadata`, {
            json: body,
          })
          .json<GenerateOrganizationAgentMetadataResponse>(),
    },
    automations: {
      list: async (
        organizationId: string,
      ): Promise<Array<OrganizationAgentTriggerResponse>> =>
        ky
          .get(`organizations/${organizationId}/agent-triggers`)
          .json<Array<OrganizationAgentTriggerResponse>>(),
      create: async (
        organizationId: string,
        body: CreateOrganizationAgentTriggerRequest,
      ): Promise<OrganizationAgentTriggerResponse> =>
        ky
          .post(`organizations/${organizationId}/agent-triggers`, {
            json: body,
          })
          .json<OrganizationAgentTriggerResponse>(),
      update: async (
        organizationId: string,
        triggerId: string,
        body: UpdateOrganizationAgentTriggerRequest,
      ): Promise<OrganizationAgentTriggerResponse> =>
        ky
          .patch(
            `organizations/${organizationId}/agent-triggers/${triggerId}`,
            { json: body },
          )
          .json<OrganizationAgentTriggerResponse>(),
      remove: async (
        organizationId: string,
        triggerId: string,
      ): Promise<void> => {
        await ky.delete(
          `organizations/${organizationId}/agent-triggers/${triggerId}`,
        )
      },
      runs: async (
        organizationId: string,
        triggerId?: string,
      ): Promise<Array<OrganizationAgentTriggerRunResponse>> =>
        ky
          .get(`organizations/${organizationId}/agent-trigger-runs`, {
            searchParams: triggerId ? { triggerId } : undefined,
          })
          .json<Array<OrganizationAgentTriggerRunResponse>>(),
      retryRun: async (
        organizationId: string,
        runId: string,
      ): Promise<OrganizationAgentTriggerRunResponse> =>
        ky
          .post(
            `organizations/${organizationId}/agent-trigger-runs/${runId}/retry`,
          )
          .json<OrganizationAgentTriggerRunResponse>(),
      previewSchedule: async (
        organizationId: string,
        body: OrganizationAgentSchedulePreviewRequest,
      ): Promise<OrganizationAgentSchedulePreviewResponse> =>
        ky
          .post(
            `organizations/${organizationId}/agent-triggers/schedule-preview`,
            { json: body },
          )
          .json<OrganizationAgentSchedulePreviewResponse>(),
    },
  },
  payments: {
    config: async (): Promise<PaymentsConfigResponse> =>
      ky.get('payments/config').json<PaymentsConfigResponse>(),
  },
  billing: {
    overview: async (
      organizationId: string,
    ): Promise<BillingOverviewResponse> =>
      ky
        .get(`organizations/${organizationId}/billing`)
        .json<BillingOverviewResponse>(),
    topUps: async (organizationId: string): Promise<Array<CreditTopUpItem>> =>
      ky
        .get(`organizations/${organizationId}/billing/top-ups`)
        .json<Array<CreditTopUpItem>>(),
    startTopUp: async (
      organizationId: string,
      body: StartTopUpRequest,
    ): Promise<PaymentCheckoutSession> =>
      ky
        .post(`organizations/${organizationId}/billing/top-ups`, {
          json: body,
          retry: 0,
        })
        .json<PaymentCheckoutSession>(),
    topUp: async (
      organizationId: string,
      paymentId: string,
    ): Promise<PaymentStatusResponse> =>
      ky
        .get(`organizations/${organizationId}/billing/top-ups/${paymentId}`)
        .json<PaymentStatusResponse>(),
    fakeCompleteTopUp: async (
      organizationId: string,
      paymentId: string,
    ): Promise<PaymentStatusResponse> =>
      ky
        .post(
          `organizations/${organizationId}/billing/top-ups/${paymentId}/fake-complete`,
          { retry: 0 },
        )
        .json<PaymentStatusResponse>(),
    requestRefund: async (
      organizationId: string,
      paymentId: string,
      body: RequestRefundRequest,
    ): Promise<CreditTopUpItem> =>
      ky
        .post(
          `organizations/${organizationId}/billing/top-ups/${paymentId}/refunds`,
          { json: body, retry: 0 },
        )
        .json<CreditTopUpItem>(),
  },
  workspaces: {
    listRules: async (id: string): Promise<WorkspaceRuleListResponse> =>
      ky.get(`workspaces/${id}/rules`).json<WorkspaceRuleListResponse>(),
    createRule: async (
      id: string,
      body: CreateWorkspaceRuleRequest,
    ): Promise<WorkspaceRuleResponse> =>
      ky
        .post(`workspaces/${id}/rules`, { json: body })
        .json<WorkspaceRuleResponse>(),
    updateRule: async (
      id: string,
      ruleId: string,
      body: UpdateWorkspaceRuleRequest,
    ): Promise<WorkspaceRuleResponse> =>
      ky
        .patch(`workspaces/${id}/rules/${ruleId}`, { json: body })
        .json<WorkspaceRuleResponse>(),
    deleteRule: async (
      id: string,
      ruleId: string,
      expectedVersion: number,
    ): Promise<void> => {
      await ky.delete(`workspaces/${id}/rules/${ruleId}`, {
        json: { expectedVersion },
      })
    },
    create: async (body: CreateWorkspaceRequest): Promise<WorkspaceResponse> =>
      normalizeWorkspace(
        await ky.post('workspaces', { json: body }).json<WorkspaceResponse>(),
      ),
    createDraft: async (
      body: CreateWorkspaceRequest,
    ): Promise<WorkspaceResponse> =>
      normalizeWorkspace(
        await ky
          .post('workspaces', { json: { ...body, isDraft: true } })
          .json<WorkspaceResponse>(),
      ),
    list: async (): Promise<Array<WorkspaceResponse>> =>
      (await ky.get('workspaces').json<Array<WorkspaceResponse>>()).map(
        normalizeWorkspace,
      ),
    getById: async (id: string): Promise<WorkspaceResponse> =>
      normalizeWorkspace(
        await ky.get(`workspaces/${id}`).json<WorkspaceResponse>(),
      ),
    update: async (
      id: string,
      body: UpdateWorkspaceRequest,
    ): Promise<WorkspaceResponse> =>
      normalizeWorkspace(
        await ky
          .patch(`workspaces/${id}`, { json: body })
          .json<WorkspaceResponse>(),
      ),
    remove: async (id: string): Promise<void> => {
      await ky.delete(`workspaces/${id}`)
    },
    leave: async (id: string): Promise<WorkspaceResponse> =>
      normalizeWorkspace(
        await ky.post(`workspaces/${id}/leave`).json<WorkspaceResponse>(),
      ),
    getJiraConnection: async (
      id: string,
    ): Promise<WorkspaceJiraConnectionStatusResponse> =>
      ky
        .get(`workspaces/${id}/jira/connection`)
        .json<WorkspaceJiraConnectionStatusResponse>(),
    startJiraOAuth: async (
      id: string,
    ): Promise<WorkspaceJiraOAuthStartResponse> =>
      ky
        .post(`workspaces/${id}/jira/oauth/start`)
        .json<WorkspaceJiraOAuthStartResponse>(),
    getJiraProjects: async (
      id: string,
    ): Promise<WorkspaceJiraProjectsResponse> =>
      ky
        .get(`workspaces/${id}/jira/projects`)
        .json<WorkspaceJiraProjectsResponse>(),
    disconnectJira: async (id: string): Promise<void> => {
      await ky.delete(`workspaces/${id}/jira/connection`)
    },
    getTelegramChats: async (
      id: string,
    ): Promise<TelegramLinkedChatsResponse> =>
      ky
        .get(`workspaces/${id}/telegram/chats`)
        .json<TelegramLinkedChatsResponse>(),
    createTelegramLinkCode: async (
      id: string,
    ): Promise<TelegramLinkCodeResponse> =>
      ky
        .post(`workspaces/${id}/telegram/link-code`)
        .json<TelegramLinkCodeResponse>(),
    updateTelegramChat: async (
      id: string,
      chatId: string,
      body: UpdateTelegramChatRequest,
    ): Promise<void> => {
      await ky.patch(
        `workspaces/${id}/telegram/chats/${encodeURIComponent(chatId)}`,
        { json: body },
      )
    },
    unlinkTelegramChat: async (id: string, chatId: string): Promise<void> => {
      await ky.delete(
        `workspaces/${id}/telegram/chats/${encodeURIComponent(chatId)}`,
      )
    },
    listDocuments: async (
      id: string,
    ): Promise<Array<WorkspaceDocumentResponse>> =>
      ky
        .get(`workspaces/${id}/documents`)
        .json<Array<WorkspaceDocumentResponse>>(),
    uploadDocument: async (
      id: string,
      file: File,
    ): Promise<WorkspaceDocumentResponse> => {
      const body = new FormData()
      body.append('file', file)
      return ky
        .post(`workspaces/${id}/documents`, { body })
        .json<WorkspaceDocumentResponse>()
    },
    deleteDocument: async (id: string, documentId: string): Promise<void> => {
      await ky.delete(`workspaces/${id}/documents/${documentId}`)
    },
    retryDocumentIndexing: async (
      id: string,
      documentId: string,
    ): Promise<WorkspaceDocumentResponse> =>
      ky
        .post(`workspaces/${id}/documents/${documentId}/retry-indexing`)
        .json<WorkspaceDocumentResponse>(),
    reindexDocument: async (
      id: string,
      documentId: string,
    ): Promise<WorkspaceDocumentResponse> =>
      ky
        .post(`workspaces/${id}/documents/${documentId}/reindex`)
        .json<WorkspaceDocumentResponse>(),
    getDocumentContent: async (id: string, documentId: string): Promise<Blob> =>
      ky.get(`workspaces/${id}/documents/${documentId}/content`).blob(),
    getConfluenceConnection: async (
      id: string,
    ): Promise<WorkspaceConfluenceConnectionStatusResponse> =>
      ky
        .get(`workspaces/${id}/confluence/connection`)
        .json<WorkspaceConfluenceConnectionStatusResponse>(),
    startConfluenceOAuth: async (
      id: string,
    ): Promise<WorkspaceConfluenceOAuthStartResponse> =>
      ky
        .post(`workspaces/${id}/confluence/oauth/start`)
        .json<WorkspaceConfluenceOAuthStartResponse>(),
    disconnectConfluence: async (id: string): Promise<void> => {
      await ky.delete(`workspaces/${id}/confluence/connection`)
    },
    getGmailConnection: async (
      id: string,
    ): Promise<WorkspaceGmailConnectionStatusResponse> =>
      ky
        .get(`workspaces/${id}/gmail/connection`)
        .json<WorkspaceGmailConnectionStatusResponse>(),
    startGmailOAuth: async (
      id: string,
    ): Promise<WorkspaceGmailOAuthStartResponse> =>
      ky
        .post(`workspaces/${id}/gmail/oauth/start`)
        .json<WorkspaceGmailOAuthStartResponse>(),
    disconnectGmail: async (
      id: string,
      connectionId: string,
    ): Promise<void> => {
      await ky.delete(`workspaces/${id}/gmail/connection/${connectionId}`)
    },
    getYandexMailConnection: async (
      id: string,
    ): Promise<WorkspaceYandexMailConnectionStatusResponse> =>
      ky
        .get(`workspaces/${id}/yandex-mail/connection`)
        .json<WorkspaceYandexMailConnectionStatusResponse>(),
    startYandexMailOAuth: async (
      id: string,
    ): Promise<WorkspaceYandexMailOAuthStartResponse> =>
      ky
        .post(`workspaces/${id}/yandex-mail/oauth/start`)
        .json<WorkspaceYandexMailOAuthStartResponse>(),
    disconnectYandexMail: async (
      id: string,
      connectionId: string,
    ): Promise<void> => {
      await ky.delete(`workspaces/${id}/yandex-mail/connection/${connectionId}`)
    },
    getCalendarConnection: async (
      id: string,
    ): Promise<WorkspaceCalendarConnectionStatusResponse> =>
      ky
        .get(`workspaces/${id}/calendar/connection`)
        .json<WorkspaceCalendarConnectionStatusResponse>(),
    startCalendarOAuth: async (
      id: string,
      body: WorkspaceCalendarOAuthStartRequest,
    ): Promise<WorkspaceCalendarOAuthStartResponse> =>
      ky
        .post(`workspaces/${id}/calendar/oauth/start`, { json: body })
        .json<WorkspaceCalendarOAuthStartResponse>(),
    disconnectCalendar: async (id: string): Promise<void> => {
      await ky.delete(`workspaces/${id}/calendar/connection`)
    },
    getGitHubConnection: async (
      id: string,
    ): Promise<WorkspaceGitHubConnectionStatusResponse> =>
      ky
        .get(`workspaces/${id}/github/connection`)
        .json<WorkspaceGitHubConnectionStatusResponse>(),
    getGitHubRepositories: async (
      id: string,
    ): Promise<WorkspaceGitHubRepositoriesResponse> =>
      ky
        .get(`workspaces/${id}/github/repositories`)
        .json<WorkspaceGitHubRepositoriesResponse>(),
    getGitHubFileContent: async (
      id: string,
      params: { owner: string; repo: string; path: string; ref?: string },
    ): Promise<WorkspaceGitHubFileContentResponse> => {
      const searchParams = new URLSearchParams({
        owner: params.owner,
        repo: params.repo,
        path: params.path,
      })
      if (params.ref) {
        searchParams.set('ref', params.ref)
      }
      return ky
        .get(`workspaces/${id}/github/file-content`, { searchParams })
        .json<WorkspaceGitHubFileContentResponse>()
    },
    startGitHubOAuth: async (
      id: string,
    ): Promise<WorkspaceGitHubOAuthStartResponse> =>
      ky
        .post(`workspaces/${id}/github/oauth/start`)
        .json<WorkspaceGitHubOAuthStartResponse>(),
    startGitHubUserOAuth: async (
      id: string,
    ): Promise<WorkspaceGitHubOAuthStartResponse> =>
      ky
        .post(`workspaces/${id}/github/oauth/user/start`)
        .json<WorkspaceGitHubOAuthStartResponse>(),
    listGitHubInstallations: async (
      id: string,
    ): Promise<{
      installations: Array<{
        installationId: string
        accountLogin: string
        accountName: string
        accountType: 'user' | 'organization'
        repositorySelection: 'all' | 'selected'
      }>
    }> => ky.get(`workspaces/${id}/github/installations`).json(),
    linkGitHubInstallation: async (
      id: string,
      installationId: string,
    ): Promise<{
      workspaceId: string
      accountLogin: string
      accountName: string
    }> =>
      ky
        .post(`workspaces/${id}/github/connection/link`, {
          json: { installationId },
        })
        .json(),
    disconnectGitHub: async (id: string): Promise<void> => {
      await ky.delete(`workspaces/${id}/github/connection`)
    },
    listUsers: async (): Promise<Array<WorkspaceUserResponse>> =>
      ky.get('workspace-users').json<Array<WorkspaceUserResponse>>(),
  },
  organizationInvites: {
    create: async (orgId: string): Promise<OrganizationInviteCreateResponse> =>
      ky
        .post(`organizations/${orgId}/invites`)
        .json<OrganizationInviteCreateResponse>(),
    getStatusByToken: async (
      token: string,
    ): Promise<OrganizationInviteStatusResponse> =>
      ky
        .get(`org-invites/${token}/status`)
        .json<OrganizationInviteStatusResponse>(),
    acceptByToken: async (
      token: string,
    ): Promise<OrganizationInviteAcceptResponse> =>
      ky
        .post(`org-invites/${token}/accept`)
        .json<OrganizationInviteAcceptResponse>(),
  },
  joinRequests: {
    listPending: async (orgId: string): Promise<Array<JoinRequestResponse>> =>
      ky
        .get(`organizations/${orgId}/join-requests`, {
          searchParams: { status: 'pending' },
        })
        .json<Array<JoinRequestResponse>>(),
    approve: async (
      orgId: string,
      requestId: string,
    ): Promise<JoinRequestActionResponse> =>
      ky
        .post(`organizations/${orgId}/join-requests/${requestId}/approve`)
        .json<JoinRequestActionResponse>(),
    reject: async (
      orgId: string,
      requestId: string,
    ): Promise<JoinRequestActionResponse> =>
      ky
        .post(`organizations/${orgId}/join-requests/${requestId}/reject`)
        .json<JoinRequestActionResponse>(),
  },
  pilotApplications: {
    submit: async (
      body: PilotApplicationRequest,
    ): Promise<PilotApplicationResponse> =>
      ky
        .post('pilot-applications', { json: body })
        .json<PilotApplicationResponse>(),
  },
  demoRequests: {
    submit: async (body: DemoRequestRequest): Promise<DemoRequestResponse> =>
      ky.post('demo-requests', { json: body }).json<DemoRequestResponse>(),
  },
  gtm: {
    createDemoRun: async (
      body: CreateGtmDemoRunRequest,
    ): Promise<GtmDemoRunResponse> =>
      ky
        .post('gtm/demo-runs', { json: body, retry: 0 })
        .json<GtmDemoRunResponse>(),
    getDemoRun: async (id: string): Promise<GtmDemoRunResponse> =>
      ky.get(`gtm/demo-runs/${id}`).json<GtmDemoRunResponse>(),
    createCheckout: async (
      body: CreateGtmCheckoutRequest,
    ): Promise<CreateGtmCheckoutResponse> =>
      ky
        .post('gtm/checkouts', { json: body, retry: 0 })
        .json<CreateGtmCheckoutResponse>(),
    getCheckout: async (id: string): Promise<GtmCheckoutResponse> =>
      ky.get(`gtm/checkouts/${id}`).json<GtmCheckoutResponse>(),
    updateCheckout: async (
      id: string,
      body: UpdateGtmCheckoutRequest,
    ): Promise<GtmCheckoutResponse> =>
      ky.patch(`gtm/checkouts/${id}`, { json: body, retry: 0 }).json(),
    /** Opens the payment provider's checkout; the visitor pays as a guest. */
    startCheckoutPayment: async (id: string): Promise<PaymentCheckoutSession> =>
      ky
        .post(`gtm/checkouts/${id}/payment`, { retry: 0 })
        .json<PaymentCheckoutSession>(),
    /** After sign-up: the paid checkout becomes the account's balance and workspace. */
    claimCheckout: async (
      id: string,
      body: ClaimGtmCheckoutRequest = {},
    ): Promise<GtmCheckoutResponse> =>
      ky
        .post(`gtm/checkouts/${id}/claim`, { json: body, retry: 0 })
        .json<GtmCheckoutResponse>(),
    /** Test payments only: completes the checkout's payment without money. */
    payFakeCheckout: async (id: string): Promise<GtmCheckoutResponse> =>
      ky
        .post(`gtm/checkouts/${id}/fake-pay`, { retry: 0 })
        .json<GtmCheckoutResponse>(),
  },
  gtmWorkspace: {
    projects: async (workspaceId: string): Promise<Array<GtmProject>> =>
      ky
        .get(`workspaces/${workspaceId}/gtm/projects`)
        .json<Array<GtmProject>>(),
    createProject: async (
      workspaceId: string,
      url: string,
    ): Promise<GtmProjectCreation> =>
      ky
        .post(`workspaces/${workspaceId}/gtm/projects`, {
          json: { url },
          retry: 0,
        })
        .json<GtmProjectCreation>(),
    analysis: async (
      workspaceId: string,
      projectId: string,
    ): Promise<GtmWorkspaceAnalysis | null> =>
      ky
        .get(`workspaces/${workspaceId}/gtm/analysis`, {
          searchParams: { projectId },
        })
        .json<GtmWorkspaceAnalysis | null>(),
    sender: async (workspaceId: string): Promise<GtmSenderStatus> =>
      ky.get(`workspaces/${workspaceId}/gtm/sender`).json<GtmSenderStatus>(),
    usage: async (workspaceId: string): Promise<GtmUsageSummary> =>
      ky.get(`workspaces/${workspaceId}/gtm/usage`).json<GtmUsageSummary>(),
    refreshInbox: async (
      workspaceId: string,
    ): Promise<GtmInboxRefreshResponse> =>
      ky
        .post(`workspaces/${workspaceId}/gtm/inbox/refresh`)
        .json<GtmInboxRefreshResponse>(),
    usageOverview: async (workspaceId: string): Promise<GtmUsageOverview> =>
      ky
        .get(`workspaces/${workspaceId}/gtm/usage/summary`)
        .json<GtmUsageOverview>(),
    sendMessage: async (
      workspaceId: string,
      projectId: string,
      messageId: string,
    ): Promise<GtmWorkspaceLeadDetail> =>
      ky
        .post(`workspaces/${workspaceId}/gtm/messages/${messageId}/send`, {
          retry: 0,
          timeout: 30_000,
          searchParams: { projectId },
        })
        .json<GtmWorkspaceLeadDetail>(),
    updateDraft: async (
      workspaceId: string,
      projectId: string,
      messageId: string,
      body: CreateGtmDraftRequest,
    ): Promise<GtmWorkspaceLeadDetail> =>
      ky
        .patch(`workspaces/${workspaceId}/gtm/messages/${messageId}/draft`, {
          json: body,
          retry: 0,
          searchParams: { projectId },
        })
        .json<GtmWorkspaceLeadDetail>(),
    createDraft: async (
      workspaceId: string,
      projectId: string,
      leadId: string,
      body: CreateGtmDraftRequest,
    ): Promise<GtmWorkspaceLeadDetail> =>
      ky
        .post(`workspaces/${workspaceId}/gtm/leads/${leadId}/drafts`, {
          json: body,
          retry: 0,
          searchParams: { projectId },
        })
        .json<GtmWorkspaceLeadDetail>(),
    overview: async (
      workspaceId: string,
      projectId: string,
    ): Promise<GtmWorkspaceOverview> =>
      ky
        .get(`workspaces/${workspaceId}/gtm`, { searchParams: { projectId } })
        .json<GtmWorkspaceOverview>(),
    updateCampaignBuyer: async (
      workspaceId: string,
      projectId: string,
      campaignId: string,
      body: UpdateGtmCampaignBuyerRequest,
    ): Promise<GtmWorkspaceCampaign> =>
      ky
        .patch(`workspaces/${workspaceId}/gtm/campaigns/${campaignId}/buyer`, {
          json: body,
          searchParams: { projectId },
          retry: 0,
        })
        .json<GtmWorkspaceCampaign>(),
    emailSettings: async (
      workspaceId: string,
      projectId: string,
    ): Promise<GtmWorkspaceEmailSettings> =>
      ky
        .get(`workspaces/${workspaceId}/gtm/email-settings`, {
          searchParams: { projectId },
        })
        .json<GtmWorkspaceEmailSettings>(),
    outreach: async (
      workspaceId: string,
      projectId: string,
    ): Promise<GtmOutreachPlan> =>
      ky
        .get(`workspaces/${workspaceId}/gtm/outreach`, {
          searchParams: { projectId },
        })
        .json<GtmOutreachPlan>(),
    updateOutreach: async (
      workspaceId: string,
      projectId: string,
      body: UpdateGtmOutreachRequest,
    ): Promise<GtmOutreachPlan> =>
      ky
        .patch(`workspaces/${workspaceId}/gtm/outreach`, {
          json: body,
          searchParams: { projectId },
          retry: 0,
        })
        .json<GtmOutreachPlan>(),
    sendingSettings: async (
      workspaceId: string,
      projectId: string,
    ): Promise<GtmSendingSettings> =>
      ky
        .get(`workspaces/${workspaceId}/gtm/sending-settings`, {
          searchParams: { projectId },
        })
        .json<GtmSendingSettings>(),
    updateSendingSettings: async (
      workspaceId: string,
      projectId: string,
      body: UpdateGtmSendingSettingsRequest,
    ): Promise<GtmSendingSettings> =>
      ky
        .patch(`workspaces/${workspaceId}/gtm/sending-settings`, {
          json: body,
          searchParams: { projectId },
          retry: 0,
        })
        .json<GtmSendingSettings>(),
    updateEmailSettings: async (
      workspaceId: string,
      projectId: string,
      body: UpdateGtmEmailSettingsRequest,
    ): Promise<GtmWorkspaceEmailSettings> =>
      ky
        .patch(`workspaces/${workspaceId}/gtm/email-settings`, {
          json: body,
          searchParams: { projectId },
          retry: 0,
        })
        .json<GtmWorkspaceEmailSettings>(),
    /** An email of the sequence in a tone (LP-289); written once, then kept. */
    emailPreview: async (
      workspaceId: string,
      projectId: string,
      body: GtmEmailPreviewRequest,
    ): Promise<GtmEmailPreview> =>
      ky
        .post(`workspaces/${workspaceId}/gtm/email-settings/preview`, {
          json: body,
          searchParams: { projectId },
          retry: 0,
          // Writing an email takes the model a while.
          timeout: 90_000,
        })
        .json<GtmEmailPreview>(),
    campaigns: async (
      workspaceId: string,
      projectId: string,
    ): Promise<Array<GtmWorkspaceCampaign>> =>
      ky
        .get(`workspaces/${workspaceId}/gtm/campaigns`, {
          searchParams: { projectId },
        })
        .json<Array<GtmWorkspaceCampaign>>(),
    leads: async (
      workspaceId: string,
      projectId: string,
      filter: { stage?: GtmLeadStage; campaignId?: string },
    ): Promise<Array<GtmWorkspaceLead>> =>
      ky
        .get(`workspaces/${workspaceId}/gtm/leads`, {
          searchParams: {
            projectId,
            ...(Object.fromEntries(
              Object.entries(filter).filter(([, value]) => Boolean(value)),
            ) as Record<string, string>),
          },
        })
        .json<Array<GtmWorkspaceLead>>(),
    conversations: async (
      workspaceId: string,
      projectId: string,
    ): Promise<Array<GtmConversationSummary>> =>
      ky
        .get(`workspaces/${workspaceId}/gtm/conversations`, {
          searchParams: { projectId },
        })
        .json<Array<GtmConversationSummary>>(),
    lead: async (
      workspaceId: string,
      projectId: string,
      leadId: string,
    ): Promise<GtmWorkspaceLeadDetail> =>
      ky
        .get(`workspaces/${workspaceId}/gtm/leads/${leadId}`, {
          searchParams: { projectId },
        })
        .json<GtmWorkspaceLeadDetail>(),
    updateLead: async (
      workspaceId: string,
      projectId: string,
      leadId: string,
      body: UpdateGtmLeadRequest,
    ): Promise<GtmWorkspaceLeadDetail> =>
      ky
        .patch(`workspaces/${workspaceId}/gtm/leads/${leadId}`, {
          json: body,
          searchParams: { projectId },
        })
        .json<GtmWorkspaceLeadDetail>(),
    hire: async (
      workspaceId: string,
      url: string,
    ): Promise<GtmWorkspaceSearch> =>
      ky
        .post(`workspaces/${workspaceId}/gtm/hire`, { json: { url }, retry: 0 })
        .json<GtmWorkspaceSearch>(),
    startSearch: async (
      workspaceId: string,
      projectId: string,
    ): Promise<GtmWorkspaceSearch> =>
      ky
        .post(`workspaces/${workspaceId}/gtm/searches`, {
          retry: 0,
          searchParams: { projectId },
        })
        .json<GtmWorkspaceSearch>(),
    latestSearch: async (
      workspaceId: string,
      projectId: string,
    ): Promise<GtmWorkspaceSearch | null> =>
      ky
        .get(`workspaces/${workspaceId}/gtm/searches/latest`, {
          searchParams: { projectId },
        })
        .json<GtmWorkspaceSearch | null>(),
    recordReply: async (
      workspaceId: string,
      projectId: string,
      leadId: string,
      text: string,
    ): Promise<GtmReplyResult> =>
      ky
        .post(`workspaces/${workspaceId}/gtm/leads/${leadId}/replies`, {
          json: { text },
          retry: 0,
          timeout: 120_000,
          searchParams: { projectId },
        })
        .json<GtmReplyResult>(),
  },
  invites: {
    getById: async (id: string): Promise<InviteResponse> =>
      ky.get(`invites/${id}`).json<InviteResponse>(),
    getByWorkspaceId: async (
      workspaceId: string,
    ): Promise<InviteResponse | null> => {
      try {
        return await ky
          .get(`invites/workspace/${workspaceId}`)
          .json<InviteResponse | null>()
      } catch {
        return null
      }
    },
    accept: async (id: string): Promise<WorkspaceResponse> =>
      normalizeWorkspace(
        await ky.post(`invites/${id}/accept`).json<WorkspaceResponse>(),
      ),
  },
  contextAudit: {
    getPreview: async (
      workspaceId: string,
    ): Promise<ContextAuditPreviewResponse> =>
      ky
        .get(`workspaces/${workspaceId}/context-audit/preview`)
        .json<ContextAuditPreviewResponse>(),
    listReports: async (
      workspaceId: string,
    ): Promise<ContextAuditReportListResponse> =>
      ky
        .get(`workspaces/${workspaceId}/context-audit/reports`)
        .json<ContextAuditReportListResponse>(),
    getLatestReport: async (
      workspaceId: string,
    ): Promise<ContextAuditReportDetailResponse | null> =>
      ky
        .get(`workspaces/${workspaceId}/context-audit/reports/latest`)
        .json<ContextAuditReportDetailResponse | null>(),
    getDrilldown: async (
      workspaceId: string,
      reportId: string,
      signal: string,
    ): Promise<ContextAuditDrilldownResponse> =>
      ky
        .get(
          `workspaces/${workspaceId}/context-audit/reports/${reportId}/drilldown`,
          { searchParams: { signal } },
        )
        .json<ContextAuditDrilldownResponse>(),
    getPreviewDrilldown: async (
      workspaceId: string,
      signal: string,
    ): Promise<ContextAuditDrilldownResponse> =>
      ky
        .get(`workspaces/${workspaceId}/context-audit/preview/drilldown`, {
          searchParams: { signal },
        })
        .json<ContextAuditDrilldownResponse>(),
    runIngest: async (workspaceId: string): Promise<ContextAuditScanResponse> =>
      ky
        .post(`workspaces/${workspaceId}/context-audit/ingest`, { json: {} })
        .json<ContextAuditScanResponse>(),
    getScan: async (
      workspaceId: string,
    ): Promise<ContextAuditScanResponse | null> =>
      ky
        .get(`workspaces/${workspaceId}/context-audit/scan`)
        .json<ContextAuditScanResponse | null>(),
    generateReport: async (
      workspaceId: string,
    ): Promise<ContextAuditReportDetailResponse> =>
      ky
        .post(`workspaces/${workspaceId}/context-audit/reports`)
        .json<ContextAuditReportDetailResponse>(),
    clearReportHistory: async (
      workspaceId: string,
    ): Promise<ContextAuditClearHistoryResponse> =>
      ky
        .delete(`workspaces/${workspaceId}/context-audit/reports`)
        .json<ContextAuditClearHistoryResponse>(),
    listDrafts: async (
      workspaceId: string,
    ): Promise<ContextAuditDraftListResponse> =>
      ky
        .get(`workspaces/${workspaceId}/context-audit/drafts`)
        .json<ContextAuditDraftListResponse>(),
    reviewDraft: async (
      workspaceId: string,
      draftId: string,
      approved: boolean,
    ): Promise<{ id: string; status: string }> =>
      ky
        .post(
          `workspaces/${workspaceId}/context-audit/drafts/${draftId}/review`,
          { json: { approved } },
        )
        .json<{ id: string; status: string }>(),
    applyDraft: async (
      workspaceId: string,
      draftId: string,
    ): Promise<{ id: string; status: string; failureReason: string | null }> =>
      ky
        .post(`workspaces/${workspaceId}/context-audit/drafts/${draftId}/apply`)
        .json<{ id: string; status: string; failureReason: string | null }>(),
    getTrend: async (workspaceId: string): Promise<ContextAuditTrendResponse> =>
      ky
        .get(`workspaces/${workspaceId}/context-audit/trend`)
        .json<ContextAuditTrendResponse>(),
  },
}
